/**
 * Mordeczka.eu Admin - Watch Brands Page with nationality
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Popconfirm,
  message,
  Card,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { watchBrandsApi, nationalitiesApi } from "../services/api";
import type { WatchBrand, WatchBrandCreate, WatchBrandUpdate } from "../types";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;

export function WatchBrandsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WatchBrand | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100 });
  const { canEdit, canDelete } = usePermissions();

  // Fetch watch brands
  const { data, isLoading } = useQuery({
    queryKey: ["watch-brands", pagination],
    queryFn: () =>
      watchBrandsApi.list({
        page: pagination.current,
        page_size: pagination.pageSize,
      }),
  });

  // Fetch nationalities for select
  const { data: nationalities } = useQuery({
    queryKey: ["nationalities-all"],
    queryFn: () => nationalitiesApi.list({ page_size: 200 }),
  });

  const createMutation = useMutation({
    mutationFn: (data: WatchBrandCreate) => watchBrandsApi.create(data),
    onSuccess: () => {
      message.success("Marka utworzona pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["watch-brands"] });
      closeModal();
    },
    onError: () => message.error("Nie udało się utworzyć marki"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: WatchBrandUpdate }) =>
      watchBrandsApi.update(id, data),
    onSuccess: () => {
      message.success("Marka zaktualizowana pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["watch-brands"] });
      closeModal();
    },
    onError: () => message.error("Nie udało się zaktualizować marki"),
  });

  const deleteMutation = useMutation({
    mutationFn: watchBrandsApi.delete,
    onSuccess: () => {
      message.success("Marka usunięta pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["watch-brands"] });
    },
    onError: () => message.error("Nie udało się usunąć marki"),
  });

  const openModal = (item?: WatchBrand) => {
    setEditingItem(item || null);
    form.setFieldsValue({
      name: item?.name || "",
      nationality_id: item?.nationality_id || null,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingItem) {
        updateMutation.mutate({ id: editingItem.id, data: values });
      } else {
        createMutation.mutate(values);
      }
    } catch {
      // Validation failed
    }
  };

  const columns: ColumnsType<WatchBrand> = [
    {
      title: "Nazwa",
      dataIndex: "name",
      key: "name",
      render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Kraj pochodzenia",
      key: "nationality",
      width: 180,
      render: (_, record) => record.nationality?.name || "—",
      sorter: (a, b) =>
        (a.nationality?.name || "").localeCompare(b.nationality?.name || ""),
    },
    {
      title: "Akcje",
      key: "actions",
      width: 120,
      render: (_: unknown, record: WatchBrand) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          />
          <Popconfirm
            title="Usunąć tę markę?"
            description="Tej operacji nie można cofnąć."
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Usuń"
            okButtonProps={{ danger: true }}
            cancelText="Anuluj"
            disabled={!canDelete}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!canDelete}
              title={!canDelete ? "Brak uprawnień do usuwania" : "Usuń"}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Marki zegarków
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openModal()}
        >
          Dodaj nową
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={data?.items || []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ["50", "100", "200"],
            showTotal: (total) => `Łącznie ${total} marek`,
            onChange: (page, pageSize) =>
              setPagination({ current: page, pageSize }),
          }}
        />
      </Card>

      <Modal
        title={editingItem ? "Edytuj markę" : "Dodaj markę"}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={closeModal}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        okButtonProps={{
          disabled: !canEdit,
          title: !canEdit ? "Brak uprawnień do edycji" : undefined,
        }}
        okText="Zapisz"
        cancelText="Anuluj"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Nazwa marki"
            rules={[
              { required: true, message: "Proszę wprowadzić nazwę marki" },
            ]}
          >
            <Input placeholder="np. Rolex, Omega, Seiko" />
          </Form.Item>

          <Form.Item name="nationality_id" label="Kraj pochodzenia">
            <Select
              placeholder="Wybierz kraj"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {nationalities?.items.map((nat) => (
                <Select.Option key={nat.id} value={nat.id}>
                  {nat.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default WatchBrandsPage;
